import React from 'react';
import { Tag } from 'antd';
import {
  createColumnButtonArr,
  createColumnDate,
  createColumnTag,
  createColumnText,
  widthOptions,
} from '../../../../components/table/function';
import { ButtonTheme, ButtonToolTip } from '../../../../components';
import { color } from '../../../../resources';
import { CLAIM_CHANNEL, CLAIM_STATUS, PROCESS_STATUS } from '../../../../resources/status';
import { getRowStockState } from '../func/stockHelper';
import { STOCK_ROW_STATE } from '../resource/stockConst';

const renderWithDetail = (main, detail) => (
  <div>
    <div>{main || '-'}</div>
    <div style={{ color: '#9e9e9e', fontSize: 12 }}>{detail}</div>
  </div>
);

export const columns = ({
  onOpenTracking,
  onOpenLineMessage,
  onOpenRemarkMessage,
  onOpenClaimEdit,
  onOpenClaimView,
  onModalProcessStatus,
  stockMap = {},
  selectedByItem = {},
  privilegeFocusType,
}) => {
  const claimStatusColumn = createColumnTag(
    'สถานะ',
    { key: 'claimStatus', text: 'claimStatusName', icon: true },
    CLAIM_STATUS,
  );
  const processStatusColumn = createColumnTag(
    'สถานะภายใน',
    { key: 'processStatus', text: 'processStatusName' },
    PROCESS_STATUS,
    {
      render: (_, record) => {
        const status = PROCESS_STATUS?.find((item) => item.key === record?.processStatus);
        return (
          <div style={{ cursor: 'pointer' }} onClick={() => onModalProcessStatus(record)}>
            <Tag color={status?.color}>{status?.label}</Tag>
          </div>
        );
      },
      filters: PROCESS_STATUS?.map(({ key, label }) => ({ text: label, value: key })),
      onFilter: (value, record) => record?.processStatus === value,
    },
  );

  return [
    {
      ...claimStatusColumn,
      width: 220,
      filters: [
        ...claimStatusColumn.filters.map(({ text, value }) => ({
          text: `สถานะ: ${text}`,
          value: `claim:${value}`,
        })),
        ...processStatusColumn.filters.map(({ text, value }) => ({
          text: `สถานะภายใน: ${text}`,
          value: `process:${value}`,
        })),
      ],
      onFilter: (value, record) =>
        String(value).startsWith('claim:')
          ? claimStatusColumn.onFilter(String(value).slice(6), record)
          : processStatusColumn.onFilter(String(value).slice(8), record),
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          {claimStatusColumn.render(_, record)}
          {processStatusColumn.render(_, record)}
        </div>
      ),
    },
    {
      title: 'สต็อกคงเหลือ',
      dataIndex: 'itemCode',
      key: 'stockAvailable',
      width: 190,
      render: (_, record) => {
        const { state, available, selected, reason } = getRowStockState({
          record,
          stockMap,
          selectedByItem,
        });
        if (state === STOCK_ROW_STATE.EXCLUDED) {
          return <span style={{ color: color.gray }}>-</span>;
        }
        if (state === STOCK_ROW_STATE.UNKNOWN || state === STOCK_ROW_STATE.STALE) {
          return (
            <ButtonToolTip toolTip={reason} placement="topLeft">
              <Tag color={'orange'}>ไม่มีข้อมูล</Tag>
            </ButtonToolTip>
          );
        }
        const stock = stockMap?.[record?.itemCode];
        const isProblem = state !== STOCK_ROW_STATE.OK;
        return (
          <div>
            <Tag color={isProblem ? color.red : color.line}>
              {`เหลือ ${available}${selected > 0 ? ` / เลือก ${selected}` : ''}`}
            </Tag>
            <div style={{ fontSize: 11, color: color.gray }}>
              {`บริษัท ${stock?.company ?? '-'} : ${stock?.availableQty ?? 0}`}
            </div>
          </div>
        );
      },
    },
    createColumnText(
      'เลขที่เคลม',
      'claimCode',
      widthOptions(190, {
        onFilter: (value, record) =>
          [record.claimCode, record.claimDate].some((item) =>
            item?.toString().toLowerCase().includes(value.toLowerCase()),
          ),
        render: (text, record) => renderWithDetail(text, `วันที่แจ้ง: ${record.claimDate || '-'}`),
      }),
    ),
    createColumnText('รหัสสินค้า', 'itemCode', widthOptions(140)),
    createColumnText('ชื่อสินค้า', 'productName', widthOptions(190)),
    createColumnText(
      'เลขที่ลงทะเบียน',
      'warrantyCode',
      widthOptions(210, {
        onFilter: (value, record) =>
          [record.warrantyCode, record.warrantyDate].some((item) =>
            item?.toString().toLowerCase().includes(value.toLowerCase()),
          ),
        render: (text, record) =>
          renderWithDetail(text, `วันที่ลงทะเบียน: ${record.warrantyDate || '-'}`),
      }),
    ),
    createColumnText(
      'ชื่อ-นามสกุล',
      'fullName',
      widthOptions(240, {
        onFilter: (value, record) =>
          [record.fullName, record.telephone, record.lineName].some((item) =>
            item?.toString().toLowerCase().includes(value.toLowerCase()),
          ),
        render: (text, record) =>
          renderWithDetail(
            text,
            [record.telephone, record.lineName && `LINE: ${record.lineName}`]
              .filter(Boolean)
              .join(' · ') || '-',
          ),
      }),
    ),
    createColumnText(
      'เลขพัสดุ',
      'trackingNumber',
      widthOptions(200, {
        onFilter: (value, record) =>
          [record.trackingNumber, record.shippingDate].some((item) =>
            item?.toString().toLowerCase().includes(value.toLowerCase()),
          ),
        render: (text, record) =>
          renderWithDetail(
            record.trackingNumber ? (
              <div style={{ cursor: 'pointer' }} onClick={() => onOpenTracking(record)}>
                <Tag color={'blue'}>{text}</Tag>
              </div>
            ) : null,
            `วันที่จัดส่ง: ${record.shippingDate || '-'}`,
          ),
      }),
    ),
    createColumnDate('วันที่เข้ารับสินค้า', 'bookingDate', widthOptions(180)),
    createColumnTag(
      'ช่องทางการเคลม',
      { key: 'channelClaimType', text: 'channelClaimName' },
      CLAIM_CHANNEL,
      {
        width: 170,
      },
    ),
    createColumnText('สาขาที่เคลม', 'storeName', widthOptions(150)),
    createColumnText('หมายเหตุการเลื่อนเคลม', 'note', widthOptions(140)),
    createColumnText('หมายเหตุ', 'remark', widthOptions(140)),
    createColumnText('ส่ง CS', 'sendCsStatusLabel', widthOptions(140)),
    createColumnText('CS ID', 'refCsId', widthOptions(140)),
    createColumnText('Journal ID', 'refJournalId', widthOptions(140)),
    ...(privilegeFocusType === 'READ'
      ? [
          createColumnButtonArr('จัดการ', 'rowNo', {
            others: {
              fixed: 'right',
              width: 230,
            },
            buttonArr: [
              {
                useFor: 'DETAIL',
                render: (text, record) => {
                  const handleClick = () => {
                    onOpenClaimView(record);
                  };
                  return (
                    <ButtonToolTip toolTip="ดูรายละเอียด" placement="topLeft">
                      <ButtonTheme useFor="DETAIL" onClick={handleClick} />
                    </ButtonToolTip>
                  );
                },
              },
            ],
          }),
        ]
      : [
          createColumnButtonArr('จัดการ', 'rowNo', {
            others: {
              fixed: 'right',
              width: 230,
            },
            buttonArr: [
              {
                useFor: 'EDIT2',
                render: (text, record) => {
                  const handleClick = () => {
                    onOpenClaimEdit(record);
                  };
                  if (record.claimStatus !== 'TRANSPORTED') {
                    return (
                      <ButtonToolTip toolTip="แก้ไข" placement="topLeft">
                        <ButtonTheme useFor="EDIT2" onClick={handleClick} />
                      </ButtonToolTip>
                    );
                  }
                },
              },
              {
                useFor: 'DETAIL',
                render: (text, record) => {
                  const handleClick = () => {
                    onOpenClaimView(record);
                  };
                  if (record.claimStatus === 'TRANSPORTED') {
                    return (
                      <ButtonToolTip toolTip="ดูรายละเอียด" placement="topLeft">
                        <ButtonTheme useFor="DETAIL" onClick={handleClick} />
                      </ButtonToolTip>
                    );
                  }
                },
              },
              {
                useFor: 'LINE2',
                render: (text, record) => (
                  <ButtonToolTip toolTip="ส่งข้อความ" placement="topLeft">
                    <ButtonTheme
                      useFor="LINE2"
                      onClick={() => {
                        onOpenLineMessage(record);
                      }}
                    />
                  </ButtonToolTip>
                ),
              },
              {
                useFor: 'EVERNOTE',
                render: (text, record) => (
                  <ButtonToolTip toolTip="หมายเหตุ" placement="topLeft">
                    <ButtonTheme
                      useFor="EVERNOTE"
                      onClick={() => {
                        onOpenRemarkMessage(record);
                      }}
                    />
                  </ButtonToolTip>
                ),
              },
            ],
          }),
        ]),
  ];
};

// ลำดับ/ชื่อคอลัมน์ Excel ใช้ร่วมกันทุกแบรนด์ (37 คอลัมน์) แบรนด์ที่ไม่มีข้อมูลจะเว้นว่าง
export const exportHeaders = [
  { dataIndex: 'claimCode', title: 'เลขที่เคลม' },
  { dataIndex: 'claimDate', title: 'วันที่เคลม' },
  { dataIndex: 'warrantyCode', title: 'เลขที่ลงทะเบียน' },
  { dataIndex: 'warrantyDate', title: 'วันที่ลงทะเบียน' },
  { dataIndex: 'fullName', title: 'ชื่อ-นามสกุล' },
  { dataIndex: 'lineName', title: 'ชื่อไลน์' },
  { dataIndex: 'lineUid', title: 'ไลน์ UID' },
  { dataIndex: 'telephone', title: 'เบอร์โทร' },
  { dataIndex: 'addressFullText', title: 'ที่อยู่จัดส่ง' },
  { dataIndex: 'subDistrictName', title: 'ตำบล/แขวง' },
  { dataIndex: 'districtName', title: 'อำเภอ/เขต' },
  { dataIndex: 'provinceName', title: 'จังหวัด' },
  { dataIndex: 'zipCode', title: 'รหัสไปรษณีย์' },
  { dataIndex: 'mobileBrandName', title: 'แบรนด์มือถือ' },
  { dataIndex: 'mobileModelName', title: 'รุ่นมือถือ' },
  { dataIndex: 'mobileBrandName2', title: 'แบรนด์มือถือ 2' },
  { dataIndex: 'mobileModelName2', title: 'รุ่นมือถือ 2' },
  { dataIndex: 'mobileBrandName3', title: 'แบรนด์มือถือ 3' },
  { dataIndex: 'mobileModelName3', title: 'รุ่นมือถือ 3' },
  { dataIndex: 'productTypeName', title: 'ประเภทสินค้า' },
  { dataIndex: 'itemCode', title: 'รหัสสินค้า' },
  { dataIndex: 'productName', title: 'ชื่อสินค้า' },
  { dataIndex: 'breakdownName', title: 'สาเหตุการเคลม' },
  { dataIndex: 'breakdownSubNameTh', title: 'สาเหตุการเคลมย่อย' },
  { dataIndex: 'detail1', title: 'รายละเอียด (อาการเสีย)' },
  { dataIndex: 'channelClaimName', title: 'ช่องทางการเคลม' },
  { dataIndex: 'storeName', title: 'สาขาที่เคลม' },
  { dataIndex: 'remainingDays', title: 'วันคงเหลือ' },
  { dataIndex: 'claimStatusName', title: 'สถานะ' },
  { dataIndex: 'processStatusName', title: 'สถานะภายใน' },
  { dataIndex: 'trackingNumber', title: 'เลขพัสดุ' },
  { dataIndex: 'shippingDate', title: 'วันที่จัดส่ง' },
  { dataIndex: 'note', title: 'หมายเหตุการเลื่อนเคลม' },
  { dataIndex: 'remark', title: 'หมายเหตุ' },
  { dataIndex: 'sendCsStatusLabel', title: 'ส่ง CS' },
  { dataIndex: 'refCsId', title: 'CS ID' },
  { dataIndex: 'refJournalId', title: 'Journal ID' },
];
